import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { designSystem, commonStyles } from '../../../styles/design-system';
import { 
  useRealTimeUpdates, 
  useNotifications, 
  formatTimestamp, 
  getStatusColor, 
  getStatusText 
} from '../../../utils/realTimeUpdates';

const ActivityContainer = styled(motion.div)`
  ${commonStyles.card}
  margin-bottom: ${designSystem.spacing.xl};
  max-height: 600px;
  display: flex;
  flex-direction: column;

  h3 {
    color: ${designSystem.colors.text.primary};
    font-family: ${designSystem.typography.fontFamily.secondary};
    font-size: ${designSystem.typography.fontSize.xl};
    margin-bottom: ${designSystem.spacing.lg};
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${designSystem.spacing.sm};
  }
`;

const ConnectionStatus = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.full};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  background: ${props => getStatusColor(props.status as any)}20;
  color: ${props => getStatusColor(props.status as any)};
  transition: all ${designSystem.transitions.normal};
`;

const LiveStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.lg};
  padding: ${designSystem.spacing.md};
  background: ${designSystem.colors.background.secondary};
  border-radius: ${designSystem.borderRadius.lg};
  border: 1px solid ${designSystem.colors.border.light};
`;

const LiveStat = styled(motion.div)`
  text-align: center;
  
  .value {
    font-size: ${designSystem.typography.fontSize.xl};
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: ${designSystem.colors.primary.main};
    margin-bottom: ${designSystem.spacing.xs};
  }
  
  .label {
    font-size: ${designSystem.typography.fontSize.xs};
    color: ${designSystem.colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const ActivityFeed = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
  
  /* Scrollbar personalizzata */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${designSystem.colors.background.secondary};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${designSystem.colors.primary.main};
    border-radius: 3px;
    
    &:hover {
      background: ${designSystem.colors.primary.light};
    }
  }
`;

const ActivityItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.md};
  padding: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.sm};
  background: ${designSystem.colors.background.tertiary};
  border-radius: ${designSystem.borderRadius.lg};
  border: 1px solid ${designSystem.colors.border.light};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${designSystem.colors.primary.main};
  }
  
  .icon {
    font-size: ${designSystem.typography.fontSize.xl};
    min-width: 40px;
    text-align: center;
  }
  
  .content {
    flex: 1;
    
    .message {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
      margin-bottom: ${designSystem.spacing.xs};
    }
    
    .timestamp {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.xs};
    }
  }
  
  .value {
    color: ${designSystem.colors.primary.main};
    font-weight: ${designSystem.typography.fontWeight.bold};
    font-size: ${designSystem.typography.fontSize.lg};
  }
`;

const NotificationControls = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  margin-top: ${designSystem.spacing.md};
  padding-top: ${designSystem.spacing.md};
  border-top: 1px solid ${designSystem.colors.border.light};
`;

const NotificationButton = styled.button<{ active: boolean }>`
  background: ${props => props.active 
    ? designSystem.colors.primary.main
    : 'transparent'};
  color: ${props => props.active 
    ? '#000000' 
    : designSystem.colors.text.secondary};
  border: 2px solid ${designSystem.colors.primary.main};
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  border-radius: ${designSystem.borderRadius.lg};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  font-size: ${designSystem.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
  
  &:hover {
    background: ${designSystem.colors.primary.main};
    color: #000000;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RealTimeActivity: React.FC = () => {
  const { data, isConnected, connectionStatus } = useRealTimeUpdates();
  const { permission, requestPermission, showNotification, isSupported } = useNotifications();

  // Mostra notifiche per nuove attività
  useEffect(() => {
    if (data.recentActivity.length > 0 && permission === 'granted') {
      const latestActivity = data.recentActivity[0];
      const timeDiff = Date.now() - new Date(latestActivity.timestamp).getTime();
      
      // Notifica solo se l'attività è recente (meno di 10 secondi)
      if (timeDiff < 10000) {
        showNotification('Nuova Attività J-O-H', {
          body: latestActivity.message,
          tag: 'activity',
          requireInteraction: false
        });
      }
    }
  }, [data.recentActivity, permission, showNotification]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'booking': return '📅';
      case 'cancellation': return '❌';
      case 'payment': return '💳';
      case 'inquiry': return '💬';
      default: return '📋';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'booking': return designSystem.colors.status.success;
      case 'cancellation': return designSystem.colors.status.error;
      case 'payment': return designSystem.colors.primary.main;
      case 'inquiry': return designSystem.colors.status.warning;
      default: return designSystem.colors.text.secondary;
    }
  };

  return (
    <ActivityContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3>
        ⚡ Attività in Tempo Reale
        <ConnectionStatus status={connectionStatus}>
          {getStatusText(connectionStatus)}
        </ConnectionStatus>
      </h3>

      <LiveStats>
        <LiveStat
          animate={{ 
            scale: data.newBookings > 0 ? [1, 1.1, 1] : 1 
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="value">{data.onlineUsers}</div>
          <div className="label">Utenti Online</div>
        </LiveStat>
        
        <LiveStat
          animate={{ 
            scale: data.newBookings > 0 ? [1, 1.1, 1] : 1 
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="value">{data.newBookings}</div>
          <div className="label">Nuove Booking</div>
        </LiveStat>
        
        <LiveStat>
          <div className="value">€{data.revenue.toLocaleString()}</div>
          <div className="label">Revenue Live</div>
        </LiveStat>
        
        <LiveStat>
          <div className="value">{data.occupancyRate}%</div>
          <div className="label">Occupancy</div>
        </LiveStat>
      </LiveStats>

      <ActivityFeed>
        <AnimatePresence mode="popLayout">
          {data.recentActivity.map((activity, index) => (
            <ActivityItem
              key={activity.id}
              initial={{ opacity: 0, x: -20, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.9 }}
              transition={{ 
                duration: 0.3,
                delay: index * 0.05
              }}
              style={{
                borderLeftColor: getActivityColor(activity.type)
              }}
            >
              <div className="icon">
                {getActivityIcon(activity.type)}
              </div>
              <div className="content">
                <div className="message">{activity.message}</div>
                <div className="timestamp">
                  {formatTimestamp(activity.timestamp)}
                </div>
              </div>
              {activity.value && (
                <div className="value">
                  €{activity.value}
                </div>
              )}
            </ActivityItem>
          ))}
        </AnimatePresence>
        
        {data.recentActivity.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: designSystem.spacing.xl,
            color: designSystem.colors.text.secondary
          }}>
            <div style={{ fontSize: designSystem.typography.fontSize['3xl'], marginBottom: designSystem.spacing.md }}>
              ⏳
            </div>
            In attesa di attività...
          </div>
        )}
      </ActivityFeed>

      {isSupported && (
        <NotificationControls>
          <NotificationButton
            active={permission === 'granted'}
            onClick={requestPermission}
            disabled={permission === 'granted'}
          >
            🔔 {permission === 'granted' ? 'Notifiche Attive' : 'Abilita Notifiche'}
          </NotificationButton>
          
          <NotificationButton
            active={isConnected}
            onClick={() => window.location.reload()}
          >
            🔄 Riconnetti
          </NotificationButton>
        </NotificationControls>
      )}
    </ActivityContainer>
  );
};

export default RealTimeActivity; 